import {
  Excalidraw,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  ExcalidrawAPIProvider,
  useExcalidrawAPI,
  ExplorerPropertiesPortalContext,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import ConfirmDialog from "@excalidraw/excalidraw/components/ConfirmDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import { ExportOverlay } from "@excalidraw/excalidraw/components/ExportOverlay";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { getFileHandleType } from "@excalidraw/excalidraw/data/blob";
import { saveSceneToFile } from "@excalidraw/excalidraw/data";
import { serializeAsJSON } from "@excalidraw/excalidraw/data/json";
import { t } from "@excalidraw/excalidraw/i18n";

import { save, share, usersIcon } from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import {
  bumpElementVersions,
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  ExcalidrawElement,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
  ExcalidrawProps,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  FIREBASE_STORAGE_PREFIXES,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import {
  ExportToExcalidrawPlus,
  exportToExcalidrawPlus,
} from "./components/ExportToExcalidrawPlus";
import { TopErrorBoundary } from "./components/TopErrorBoundary";

import {
  exportToBackend,
  getCollaborationLinkData,
  importFromBackend,
  isCollaborationLink,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import { FileStatusStore } from "./data/fileStatusStore";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { createSceneFileAutoSaver } from "./data/fileAutoSave";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { useHandleDevicePreview } from "./useHandleDevicePreview";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import { ExcalidrawPlusIframeExport } from "./ExcalidrawPlusIframeExport";
import LibrariesOnePaper from "./biblioteca/LibrariesOnePaper";

import "./index.scss";

import { AppSidebar } from "./components/AppSidebar";

import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: Omit<
    RestoredDataState,
    // we're not storing files in the scene database/localStorage, and instead
    // fetch them async from a different store
    "files"
  > & {
    scrollToContent?: boolean;
  } = {
    elements: restoreElements(localDataState?.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: restoreAppState(localDataState?.appState, null),
  };

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        const imported = await importFromBackend(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
        );

        scene = {
          elements: bumpElementVersions(
            restoreElements(imported.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            localDataState?.elements,
          ),
          appState: restoreAppState(
            imported.appState,
            // local appState when importing from backend to ensure we restore
            // localStorage user settings which we do not persist on server.
            localDataState?.appState,
          ),
        };
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const excalidrawAPI = useExcalidrawAPI();

  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = isRunningInIframe();

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();
  const { devicePreviewMode, devicePreviewWidth, setDevicePreviewMode } =
    useHandleDevicePreview();

  const [langCode, setLangCode] = useAppLangCode();

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);
  const sceneFileAutoSaverRef = useRef<ReturnType<
    typeof createSceneFileAutoSaver
  > | null>(null);
  if (!sceneFileAutoSaverRef.current) {
    sceneFileAutoSaverRef.current = createSceneFileAutoSaver();
  }
  const flushSceneFileAutoSave = useCallback(() => {
    return sceneFileAutoSaverRef.current?.flush();
  }, []);

  const [isProjectDirty, setIsProjectDirty] = useState(false);
  const isProjectDirtyRef = useRef(false);
  const [isSavingProject, setIsSavingProject] = useState(false);
  const isSavingProjectRef = useRef(false);
  const [isSaveBeforeSceneChangeOpen, setIsSaveBeforeSceneChangeOpen] =
    useState(false);
  const pendingSceneChangeResolveRef = useRef<((value: boolean) => void) | null>(
    null,
  );
  const sceneSaveSnapshotRef = useRef("");
  const sceneSaveInitializedRef = useRef(false);
  const sceneTransitionPendingRef = useRef(false);
  const sceneTransitionSnapshotRef = useRef("");

  const setProjectDirty = useCallback((value: boolean) => {
    isProjectDirtyRef.current = value;
    setIsProjectDirty(value);
  }, []);

  const setSavingProject = useCallback((value: boolean) => {
    isSavingProjectRef.current = value;
    setIsSavingProject(value);
  }, []);

  const normalizeFilesForSaveState = useCallback((files: BinaryFiles) => {
    return Object.fromEntries(
      Object.entries(files).map(([fileId, fileData]) => [
        fileId,
        fileData
          ? {
              ...fileData,
              lastRetrieved: undefined,
            }
          : fileData,
      ]),
    ) as BinaryFiles;
  }, []);

  const getSceneSaveSnapshot = useCallback(
    (
      scene: {
        elements: readonly OrderedExcalidrawElement[];
        appState: AppState;
        files: BinaryFiles;
      },
      opts?: { normalizeFileMetadata?: boolean },
    ) => {
      const normalizedElements = scene.elements.map((element) =>
        "status" in element && element.status !== undefined
          ? { ...element, status: undefined }
          : element,
      ) as readonly ExcalidrawElement[];

      return serializeAsJSON(
        normalizedElements,
        scene.appState,
        opts?.normalizeFileMetadata === false
          ? scene.files
          : normalizeFilesForSaveState(scene.files),
        "local",
      );
    },
    [normalizeFilesForSaveState],
  );

  const getCurrentSceneSaveSnapshot = useCallback(() => {
    if (!excalidrawAPI) {
      return "";
    }

    return getSceneSaveSnapshot({
      elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
      appState: excalidrawAPI.getAppState(),
      files: excalidrawAPI.getFiles(),
    });
  }, [excalidrawAPI, getSceneSaveSnapshot]);

  const markSceneAsSaved = useCallback(
    (snapshot?: string) => {
      const nextSnapshot = snapshot ?? getCurrentSceneSaveSnapshot();
      if (!nextSnapshot) {
        return;
      }

      sceneSaveSnapshotRef.current = nextSnapshot;
      sceneSaveInitializedRef.current = true;
      sceneTransitionPendingRef.current = false;
      sceneTransitionSnapshotRef.current = "";
      setProjectDirty(false);
    },
    [getCurrentSceneSaveSnapshot, setProjectDirty],
  );

  const beginSceneTransition = useCallback(() => {
    sceneTransitionPendingRef.current = true;
    sceneTransitionSnapshotRef.current = getCurrentSceneSaveSnapshot();
  }, [getCurrentSceneSaveSnapshot]);

  const updateSceneDirtyState = useCallback(
    (snapshot: string) => {
      if (!sceneSaveInitializedRef.current) {
        sceneSaveSnapshotRef.current = snapshot;
        sceneSaveInitializedRef.current = true;
        setProjectDirty(false);
        return;
      }

      if (sceneTransitionPendingRef.current) {
        if (snapshot === sceneTransitionSnapshotRef.current) {
          return;
        }

        sceneTransitionPendingRef.current = false;
        sceneTransitionSnapshotRef.current = "";
        sceneSaveSnapshotRef.current = snapshot;
        sceneSaveInitializedRef.current = true;
        setProjectDirty(false);
        return;
      }

      setProjectDirty(snapshot !== sceneSaveSnapshotRef.current);
    },
    [setProjectDirty],
  );

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);
  const [explorerPortalContainer, setExplorerPortalContainer] =
    useState<HTMLDivElement | null>(null);
  const [explorerPortalOpenPopup, setExplorerPortalOpenPopup] =
    useState<AppState["openPopup"]>(null);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  // ---------------------------------------------------------------------------
  // Hoisted loadImages
  // ---------------------------------------------------------------------------
  const loadImages = useCallback(
    (data: ResolutionType<typeof initializeScene>, isInitialLoad = false) => {
      if (!data.scene || !excalidrawAPI) {
        return;
      }

      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          if (fileIds.length) {
            // Direct Firebase call (not through FileManager), so track manually
            FileStatusStore.updateStatuses(
              fileIds.map((id) => [id, "loading"]),
            );
          }
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
            FileStatusStore.updateStatuses([
              ...loadedFiles.map((f) => [f.id, "loaded"] as [FileId, "loaded"]),
              ...[...erroredFiles.keys()].map(
                (id) => [id, "error"] as [FileId, "error"],
              ),
            ]);
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(async ({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({
            currentFileIds: fileIds,
          });
        }
      }
    },
    [collabAPI, excalidrawAPI],
  );

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    initializeScene({ collabAPI, excalidrawAPI }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, excalidrawAPI }).then((data) => {
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              elements: restoreElements(data.scene.elements, null, {
                repairBindings: true,
              }),
              appState: restoreAppState(data.scene.appState, null),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
      sceneFileAutoSaverRef.current?.flush();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
      sceneFileAutoSaverRef.current?.cancel();
    };
  }, [isCollabDisabled, collabAPI, excalidrawAPI, setLangCode, loadImages]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      const shouldPreventUnload =
        isProjectDirtyRef.current ||
        isSavingProjectRef.current ||
        (excalidrawAPI
          ? LocalData.fileStorage.shouldPreventUnload(
              excalidrawAPI.getSceneElements(),
            )
          : false);

      if (shouldPreventUnload) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    const currentSceneSnapshot = getSceneSaveSnapshot({
      elements,
      appState,
      files,
    });
    updateSceneDirtyState(currentSceneSnapshot);

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });

      if (getFileHandleType(appState.fileHandle) === "onepaper") {
        sceneFileAutoSaverRef.current?.schedule({
          data: { elements, appState, files },
          filename: excalidrawAPI?.getName() ?? appState.name ?? "OnePaper",
          sourceFileHandle: appState.fileHandle,
        });
      } else {
        sceneFileAutoSaverRef.current?.cancel();
      }
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  const saveCurrentProject = useCallback(
    async (opts?: { showOverlay?: boolean; showToast?: boolean }) => {
      if (!excalidrawAPI) {
        return false;
      }

      const currentAppState = excalidrawAPI.getAppState();
      const currentFileHandleType = getFileHandleType(currentAppState.fileHandle);
      const sceneSnapshot = {
        elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
        appState: currentAppState,
        files: excalidrawAPI.getFiles(),
      };

      if (opts?.showOverlay) {
        setSavingProject(true);
      }

      try {
        if (currentFileHandleType === "onepaper") {
          const saved = await flushSceneFileAutoSave();
          if (!saved) {
            return false;
          }

          if (opts?.showToast !== false) {
            excalidrawAPI.setToast({
              message: t("toast.fileSaved"),
              duration: 1500,
            });
          }
          markSceneAsSaved(getSceneSaveSnapshot(sceneSnapshot));
          return true;
        }

        const { fileHandle } = await saveSceneToFile({
          data: sceneSnapshot,
          filename: excalidrawAPI.getName(),
          sourceFileHandle: currentAppState.fileHandle,
          fileHandle: currentAppState.fileHandle,
        });

        if (fileHandle && fileHandle !== currentAppState.fileHandle) {
          excalidrawAPI.updateScene({
            appState: {
              fileHandle,
            },
          });
        }

        if (opts?.showToast !== false) {
          excalidrawAPI.setToast({
            message: t("toast.fileSaved"),
            duration: 1500,
          });
        }
        markSceneAsSaved(getSceneSaveSnapshot(sceneSnapshot));
        return true;
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          console.error(error);
          excalidrawAPI.updateScene({
            appState: {
              errorMessage: error?.message ?? String(error),
            },
          });
        } else {
          console.warn(error);
        }
        return false;
      } finally {
        if (opts?.showOverlay) {
          setSavingProject(false);
        }
      }
    },
    [
      excalidrawAPI,
      flushSceneFileAutoSave,
      getSceneSaveSnapshot,
      markSceneAsSaved,
      setSavingProject,
    ],
  );

  const handleBeforeSceneChange = useCallback(async () => {
    if (!excalidrawAPI) {
      return true;
    }

    if (getFileHandleType(excalidrawAPI.getAppState().fileHandle) === "onepaper") {
      const saved = await saveCurrentProject({ showToast: false });
      if (!saved) {
        return false;
      }

      beginSceneTransition();
      return true;
    }

    if (!isProjectDirtyRef.current) {
      beginSceneTransition();
      return true;
    }

    return await new Promise<boolean>((resolve) => {
      pendingSceneChangeResolveRef.current = resolve;
      setIsSaveBeforeSceneChangeOpen(true);
    });
  }, [excalidrawAPI, saveCurrentProject]);

  const handleSaveBeforeSceneChange = useCallback(async () => {
    setIsSaveBeforeSceneChangeOpen(false);
    const resolve = pendingSceneChangeResolveRef.current;
    pendingSceneChangeResolveRef.current = null;

    if (!resolve) {
      return;
    }

    const saved = await saveCurrentProject({ showOverlay: true });
    if (!saved) {
      resolve(false);
      return;
    }

    beginSceneTransition();
    resolve(true);
  }, [beginSceneTransition, saveCurrentProject]);

  const handleDiscardBeforeSceneChange = useCallback(() => {
    setIsSaveBeforeSceneChangeOpen(false);
    beginSceneTransition();
    const resolve = pendingSceneChangeResolveRef.current;
    pendingSceneChangeResolveRef.current = null;
    resolve?.(true);
  }, [beginSceneTransition]);

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // ---------------------------------------------------------------------------
  // onExport — intercepts file save to wait for pending image loads
  // ---------------------------------------------------------------------------
  const onExport: Required<ExcalidrawProps>["onExport"] = useCallback(
    async function* () {
      let snapshot = FileStatusStore.getSnapshot();
      const { pending, total } = FileStatusStore.getPendingCount(
        snapshot.value,
      );
      if (pending === 0) {
        return;
      }

      // Yield initial progress
      yield {
        type: "progress",
        progress: (total - pending) / total,
        message: `Loading images (${total - pending}/${total})...`,
      };

      // Wait for all pending images to finish
      while (true) {
        snapshot = await FileStatusStore.pull(snapshot.version);
        const { pending: nowPending, total: nowTotal } =
          FileStatusStore.getPendingCount(snapshot.value);

        yield {
          type: "progress",
          progress: (nowTotal - nowPending) / nowTotal,
          message: `Loading images (${nowTotal - nowPending}/${nowTotal})...`,
        };

        if (nowPending === 0) {
          await new Promise((r) => setTimeout(r, 500));
          yield {
            type: "progress",
            message: `Preparing export...`,
          };
          return;
        }
      }
    },
    [],
  );

  const libraryReturnUrl = new URL(
    "/biblioteca/libraries.onepaper",
    window.location.origin,
  ).toString();

  const devicePreviewViewportStyle =
    devicePreviewWidth == null
      ? {
          width: "100%",
          height: "100%",
        }
      : {
          width: "100%",
          maxWidth: `${devicePreviewWidth}px`,
          height: "100%",
          margin: "0 auto",
        };

  // const onExport = () => {
  //   return new Promise((r) => setTimeout(r, 2500));
  //   // console.log("onExport");
  // };

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <div
        className={clsx("excalidraw-app__viewport", {
          "excalidraw-app__viewport--device-preview":
            devicePreviewMode !== "desktop",
        })}
        data-device-preview-mode={devicePreviewMode}
        style={devicePreviewViewportStyle}
      >
        {/* Dev-only viewport preview changes the real container width so the
            editor reacts with the same breakpoints it would see on a device. */}
        <ExplorerPropertiesPortalContext.Provider
          value={{
            container: explorerPortalContainer,
            openPopup: explorerPortalOpenPopup,
            setOpenPopup: setExplorerPortalOpenPopup,
            setContainer: setExplorerPortalContainer,
          }}
        >
          <Excalidraw
            onChange={onChange}
            onBeforeSceneChange={handleBeforeSceneChange}
            onExport={onExport}
            initialData={initialStatePromiseRef.current.promise}
            libraryReturnUrl={libraryReturnUrl}
            isCollaborating={isCollaborating}
            onPointerUpdate={collabAPI?.onPointerUpdate}
            UIOptions={{
              canvasActions: {
                toggleTheme: true,
                export: {
                  onExportToBackend,
                  renderCustomUI: excalidrawAPI
                    ? (elements, appState, files) => {
                        return (
                          <ExportToExcalidrawPlus
                            elements={elements}
                            appState={appState}
                            files={files}
                            name={excalidrawAPI.getName()}
                            onError={(error) => {
                              excalidrawAPI?.updateScene({
                                appState: {
                                  errorMessage: error.message,
                                },
                              });
                            }}
                            onSuccess={() => {
                              excalidrawAPI.updateScene({
                                appState: { openDialog: null },
                              });
                            }}
                          />
                        );
                      }
                    : undefined,
                },
              },
            }}
            langCode={langCode}
            renderCustomStats={renderCustomStats}
            detectScroll={false}
            handleKeyboardGlobally={true}
            autoFocus={true}
            theme={editorTheme}
            onThemeChange={setAppTheme}
            renderTopRightUI={(isMobile) => {
              if (isMobile) {
                return null;
              }

              return (
                <>
                  {isProjectDirty && !isSavingProject && (
                    <button
                      type="button"
                      title="Salvar"
                      aria-label="Salvar"
                      className="sidebar-trigger__label-element"
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        cursor: "pointer",
                        display: "flex",
                        marginRight: "0.5rem",
                      }}
                      onClick={() => {
                        void saveCurrentProject({ showOverlay: true });
                      }}
                    >
                      <div
                        className={clsx("sidebar-trigger")}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.35rem",
                          paddingInline: "0.85rem",
                        }}
                      >
                        <div>{save}</div>
                        <span style={{ fontWeight: 600 }}>Salvar</span>
                      </div>
                    </button>
                  )}
                  {collabAPI && !isCollabDisabled && collabError.message ? (
                    <div className="excalidraw-ui-top-right">
                      <CollabError collabError={collabError} />
                    </div>
                  ) : null}
                </>
              );
            }}
            onLinkOpen={(element, event) => {
              if (element.link && isElementLink(element.link)) {
                event.preventDefault();
                excalidrawAPI?.scrollToContent(element.link, { animate: true });
              }
            }}
          >
            <AppMainMenu
              onCollabDialogOpen={onCollabDialogOpen}
              isCollaborating={isCollaborating}
              isCollabEnabled={!isCollabDisabled}
              theme={appTheme}
              refresh={() => forceRefresh((prev) => !prev)}
              devicePreviewMode={devicePreviewMode}
              onDevicePreviewModeChange={setDevicePreviewMode}
            />
            <OverwriteConfirmDialog>
              <OverwriteConfirmDialog.Actions.ExportToImage />
              <OverwriteConfirmDialog.Actions.SaveToDisk />
              {excalidrawAPI && (
                <OverwriteConfirmDialog.Action
                  title={t("overwriteConfirm.action.excalidrawPlus.title")}
                  actionLabel={t(
                    "overwriteConfirm.action.excalidrawPlus.button",
                  )}
                  onClick={() => {
                    exportToExcalidrawPlus(
                      excalidrawAPI.getSceneElements(),
                      excalidrawAPI.getAppState(),
                      excalidrawAPI.getFiles(),
                      excalidrawAPI.getName(),
                    );
                  }}
                >
                  {t("overwriteConfirm.action.excalidrawPlus.description")}
                </OverwriteConfirmDialog.Action>
              )}
            </OverwriteConfirmDialog>
            <AppFooter onChange={() => excalidrawAPI?.refresh()} />
            {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

            <TTDDialogTrigger />
            {isSaveBeforeSceneChangeOpen && (
              <ConfirmDialog
                title="Deseja salvar as alterações antes de continuar?"
                confirmText="Salvar"
                cancelText="Não salvar"
                onConfirm={handleSaveBeforeSceneChange}
                onCancel={handleDiscardBeforeSceneChange}
              >
                <p>Deseja salvar as alterações antes de continuar?</p>
              </ConfirmDialog>
            )}
            {isSavingProject && (
              <ExportOverlay theme={editorTheme} message="Salvando arquivo..." />
            )}
            {isCollaborating && isOffline && (
              <div className="alertalert--warning">
                {t("alerts.collabOfflineWarning")}
              </div>
            )}
            {localStorageQuotaExceeded && (
              <div className="alert alert--danger">
                {t("alerts.localStorageQuotaExceeded")}
              </div>
            )}
            {latestShareableLink && (
              <ShareableLinkDialog
                link={latestShareableLink}
                onCloseRequest={() => setLatestShareableLink(null)}
                setErrorMessage={setErrorMessage}
              />
            )}
            {excalidrawAPI && !isCollabDisabled && (
              <Collab excalidrawAPI={excalidrawAPI} />
            )}

            <ShareDialog
              collabAPI={collabAPI}
              onExportToBackend={async () => {
                if (excalidrawAPI) {
                  try {
                    await onExportToBackend(
                      excalidrawAPI.getSceneElements(),
                      excalidrawAPI.getAppState(),
                      excalidrawAPI.getFiles(),
                    );
                  } catch (error: any) {
                    setErrorMessage(error.message);
                  }
                }
              }}
            />

            <AppSidebar
              refresh={() => forceRefresh((prev) => !prev)}
              devicePreviewMode={devicePreviewMode}
              onDevicePreviewModeChange={setDevicePreviewMode}
            />

            {errorMessage && (
              <ErrorDialog onClose={() => setErrorMessage("")}>
                {errorMessage}
              </ErrorDialog>
            )}

            <CommandPalette
              customCommandPaletteItems={[
                {
                  label: t("labels.liveCollaboration"),
                  category: DEFAULT_CATEGORIES.app,
                  keywords: [
                    "team",
                    "multiplayer",
                    "share",
                    "public",
                    "session",
                    "invite",
                  ],
                  icon: usersIcon,
                  perform: () => {
                    setShareDialogState({
                      isOpen: true,
                      type: "collaborationOnly",
                    });
                  },
                },
                {
                  label: t("roomDialog.button_stopSession"),
                  category: DEFAULT_CATEGORIES.app,
                  predicate: () => !!collabAPI?.isCollaborating(),
                  keywords: [
                    "stop",
                    "session",
                    "end",
                    "leave",
                    "close",
                    "exit",
                    "collaboration",
                  ],
                  perform: () => {
                    if (collabAPI) {
                      collabAPI.stopCollaboration();
                      if (!collabAPI.isCollaborating()) {
                        setShareDialogState({ isOpen: false });
                      }
                    }
                  },
                },
                {
                  label: t("labels.share"),
                  category: DEFAULT_CATEGORIES.app,
                  predicate: true,
                  icon: share,
                  keywords: [
                    "link",
                    "shareable",
                    "readonly",
                    "export",
                    "publish",
                    "snapshot",
                    "url",
                    "collaborate",
                    "invite",
                  ],
                  perform: async () => {
                    setShareDialogState({ isOpen: true, type: "share" });
                  },
                },
                {
                  label: t("labels.installPWA"),
                  category: DEFAULT_CATEGORIES.app,
                  predicate: () => !!pwaEvent,
                  perform: () => {
                    if (pwaEvent) {
                      pwaEvent.prompt();
                      pwaEvent.userChoice.then(() => {
                        // event cannot be reused, but we'll hopefully
                        // grab new one as the event should be fired again
                        pwaEvent = null;
                      });
                    }
                  },
                },
              ]}
            />
            {isVisualDebuggerEnabled() && excalidrawAPI && (
              <DebugCanvas
                appState={excalidrawAPI.getAppState()}
                scale={window.devicePixelRatio}
                ref={debugCanvasRef}
              />
            )}
          </Excalidraw>
        </ExplorerPropertiesPortalContext.Provider>
      </div>
    </div>
  );
};

const ExcalidrawApp = () => {
  const isLibraryPlaceholderWindow =
    window.location.pathname.replace(/\/+$/, "") ===
    "/biblioteca/libraries.onepaper";
  const isCloudExportWindow =
    window.location.pathname === "/excalidraw-plus-export";
  if (isLibraryPlaceholderWindow) {
    return <LibrariesOnePaper />;
  }
  if (isCloudExportWindow) {
    return <ExcalidrawPlusIframeExport />;
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawAPIProvider>
          <ExcalidrawWrapper />
        </ExcalidrawAPIProvider>
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
